import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  OgBerekendeWaarde,
  OnroerendeZaak,
  OnroerendeZaakLeegstand,
  VerhuurdOfVerpachtStatus
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    Observable,
    shareReplay,
    Subscription,
    switchMap, take,
    tap
} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiTooltipModule} from '@snelstart/ui-tooltip';
import {CommonModule} from '@angular/common';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiCardModule} from '@snelstart/ui-card';
import {SharedUiIconModule} from '@snelstart/ui-icon';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {optionsLandcodesMetNLD} from "../../gedeelde-componenten/gedeelde-componenten.component";

type OnroerendeZaakFormGroup = FormGroup<{
  adres: AdresFormGroup;
  woz: FormControl<number | null>;
  wozPlusEen: FormControl<number | null>;
  woznummer: FormControl<string | null>;
  aanhorigheid: FormControl<boolean | null>;
  percentageEigendom: FormControl<number | null>;
  bewoondePeriode: FormGroup<{
    begindatum: FormControl<Date | null>;
    einddatum: FormControl<Date | null>;
  }>;
  leegstand: FormControl<OnroerendeZaakLeegstand | null>;
  datumGekocht: FormControl<Date | null>;
  aankoopkosten: FormControl<number | null>;
  datumVerkocht: FormControl<Date | null>;
  eigenWoningSchuldVerkochteWoning: FormControl<number | null>;
  verkoopkosten: FormControl<number | null>;
  betaaldeErfpachtCanon: FormControl<number | null>;
  betaaldeErfpachtCanonEindeJaar: FormControl<number | null>;
  soortPand: FormControl<boolean | null>;
  verhuurdOfVerpacht: VerhuurdVerpachtFormGroup;
  verhuurdOfVerpachtOpEinddatum: VerhuurdVerpachtFormGroup;
  investeringen: FormControl<number | null>;
  waardestijgingWOZwaardeWegensInvesteringen: FormControl<number | null>;
  woning: FormControl<boolean | null>;
  directRendement: FormControl<number | null>;
  isVerhuurdOfVerpacht: FormControl<true | null>;
}>;

type AdresFormGroup = FormGroup<{
  straat: FormControl<string | null>;
  huisNummer: FormControl<number | null>;
  huisNummerToevoeging: FormControl<string | null>;
  postcode: FormControl<string | null>;
  woonplaats: FormControl<string | null>;
  landCode: FormControl<string | null>;
}>;

type VerhuurdVerpachtFormGroup = FormGroup<{
  status: FormControl<VerhuurdOfVerpachtStatus | null>;
  overeenkomstMeerDan12Jaar: FormControl<boolean | null>;
  jaarpacht: FormControl<number | null>;
  onzakelijkePacht: FormControl<boolean | null>;
  huurbescherming: FormControl<boolean | null>;
  jaarhuur: FormControl<number | null>;
  onzakelijkeHuur: FormControl<boolean | null>;
  zelfstandigDeelVanGroterGebouw: FormControl<boolean | null>;
  percentageVerhuurd: FormControl<number | null>;
  directRendement: FormControl<number | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-box3-overige-onroerende-zaken',
    templateUrl: './box3-overige-onroerende-zaken.component.html',
    styleUrls: ['./box3-overige-onroerende-zaken.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiDetailsModule,
        SharedUiDialogModule,
        SharedUiButtonModule,
        SharedUiTooltipModule,
        CommonModule,
        SharedUiAlertModule,
        SharedUiCardModule,
        SharedUiIconModule,
    ],
})
export class Box3OverigeOnroerendeZakenComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    protected readonly VerhuurdOfVerpachtStatus = VerhuurdOfVerpachtStatus;
    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private changeSubscriptions = new Subscription();
    public index = 0;
    public heeftOWR = false;
    public dialogGegevensVisible = false;
    public dialogOwrVisible = false;
    public rapport: any;
    public jaar: any;

    public formGroup = new FormGroup({
        box3OverigeOnroerendeZaken: new FormArray<OnroerendeZaakFormGroup>([]),
    });

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: false, label: 'Nee' },
        { value: true, label: 'Ja' },
    ];

    public optionsJaNeeWoning: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja, betreft een woning/bouwkavel' },
        { value: false, label: 'Nee, betreft een andere onroerende zaak' },
    ];

    public verhuurdOfVerpachtStatus: SelectFormControlItem<string>[] = [
        { value: VerhuurdOfVerpachtStatus.VERHUURD, label: 'Verhuurde woning' },
        { value: VerhuurdOfVerpachtStatus.VERPACHT, label: 'Verpachte woning' },
    ];

    public optionsJaNeeVerhuurZelfstandigDeel: SelectFormControlItem<boolean>[] = [
        { value: false, label: 'Ja, verhuur betreft gehele woning' },
        { value: true, label: 'Nee, verhuur betreft zelfstandig deel' },
    ];

    public optionsJaNeeHuurbescherming: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja, recht op huurbescherming' },
        { value: false, label: 'Nee, geen recht op huurbescherming' },
    ];

    public optionsJaNeeOnzakelijkeHuur: SelectFormControlItem<boolean>[] = [
        { value: false, label: 'Nee, huurpijs is zakelijk' },
        { value: true, label: 'Ja, huurprijs is onzakelijk' },
    ];

    public optionsJaNeePachtZelfstandigDeel: SelectFormControlItem<boolean>[] = [
        { value: false, label: 'Ja, pacht betreft gehele woning' },
        { value: true, label: 'Nee, pacht betreft zelfstandig deel' },
    ];
    public optionsJaNeeOvereenkomst: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja, minimaal 12 jaar' },
        { value: false, label: 'Nee, korter dan 12 jaar' },
    ];

    public optionsJaNeeOnzakelijkePacht: SelectFormControlItem<boolean>[] = [
        { value: false, label: 'Ja, pacht is zakelijk' },
        { value: true, label: 'Nee, pacht is niet zakelijk' },
    ];

    public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public ngOnInit(): void {
        this.incomeTaxEntity$
            .pipe(
                take(1), // ← toevoegen
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((entity) => {
                this.updateForm(entity.aangifte?.box3?.overigeOnroerendeZaken ?? []);
                this.jaar = entity.year;
                this.heeftOWR = entity.aangifte?.box3?.berekenWerkelijkRendement ?? false;
            });
        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        const extractRelevant = () =>
            this.box3OverigeOnroerendeZaken.getRawValue().map((w) => ({
                woz: w.woz,
                betaaldeErfpachtCanon: w.betaaldeErfpachtCanon,
                percentageEigendom: w.percentageEigendom,
            }));

        this.changeSubscriptions.add(
            this.box3OverigeOnroerendeZaken.valueChanges
                .pipe(
                    debounceTime(500),
                    tap(() => this.saveForm()),
                    map(() => extractRelevant()),
                    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                    switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(),
        );
    }

    showDialogGegevens(index: number) {
        this.index = index;
        this.dialogGegevensVisible = true;
    }

    showDialogOwr(index: number) {
        this.index = index;
        this.dialogOwrVisible = true;
    }

    get box3OverigeOnroerendeZaken(): FormArray<OnroerendeZaakFormGroup> {
        return this.formGroup.controls.box3OverigeOnroerendeZaken;
    }

    addRij(): void {
        this.box3OverigeOnroerendeZaken.push(this.createFormGroup(), { emitEvent: false });
    }

    verwijderRij(index: number): void {
        this.box3OverigeOnroerendeZaken.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
        this.subscribeToChanges();
    }

    createFormGroup(item?: OnroerendeZaak): FormGroup {
        return new FormGroup({
            adres: new FormGroup({
                straat: new FormControl<string | null>(item?.adres?.straat ?? null),
                huisNummer: new FormControl<number | null>(item?.adres?.huisNummer ?? null),
                huisNummerToevoeging: new FormControl<string | null>(item?.adres?.huisNummerToevoeging ?? null),
                postcode: new FormControl<string | null>(this.formatPostcode(item?.adres?.postcode) ?? null),
                woonplaats: new FormControl<string | null>(item?.adres?.woonplaats ?? null),
                landCode: new FormControl<string | null>(item?.adres?.landCode ?? 'NLD'),
            }),
            woz: new FormControl<number | null>(item?.woz ?? null),
            wozPlusEen: new FormControl<number | null>(item?.wozPlusEen ?? null),
            woznummer: new FormControl<string | null>(null),
            aanhorigheid: new FormControl<boolean | null>(item?.aanhorigheid ?? false),
            percentageEigendom: new FormControl<number | null>(item?.percentageEigendom ?? 100),
            bewoondePeriode: new FormGroup({
                begindatum: new FormControl<Date | null>(item?.bewoondePeriode?.begindatum ?? null),
                einddatum: new FormControl<Date | null>(item?.bewoondePeriode?.einddatum ?? null),
            }),
            leegstand: new FormControl<OnroerendeZaakLeegstand | null>(item?.leegstand ?? null),
            datumGekocht: new FormControl<Date | null>(item?.datumGekocht ?? null),
            aankoopkosten: new FormControl<number | null>(item?.aankoopkosten?.[0]?.bedrag ?? null),
            datumVerkocht: new FormControl<Date | null>(item?.datumVerkocht ?? null),
            eigenWoningSchuldVerkochteWoning: new FormControl<number | null>(item?.eigenWoningSchuldVerkochteWoning ?? null),
            verkoopkosten: new FormControl<number | null>(item?.verkoopkosten?.[0]?.bedrag ?? null),
            betaaldeErfpachtCanon: new FormControl<number | null>(item?.betaaldeErfpachtCanon ?? null),
            betaaldeErfpachtCanonEindeJaar: new FormControl<number | null>(item?.betaaldeErfpachtCanonEindeJaar ?? null),
            soortPand: new FormControl<boolean | null>(item?.soortPand ?? null),
            investeringen: new FormControl<number | null>(item?.investeringen ?? null),
            waardestijgingWOZwaardeWegensInvesteringen: new FormControl<number | null>(
                item?.waardestijgingWOZwaardeWegensInvesteringen ?? null,
            ),
            woning: new FormControl<boolean | null>(item?.woning ?? null),
            directRendement: new FormControl<number | null>(item?.directRendement ?? null),
            isVerhuurdOfVerpacht: new FormControl<boolean | null>(item?.verhuurdOfVerpacht?.status != null),
            verhuurdOfVerpacht: new FormGroup({
                status: new FormControl<VerhuurdOfVerpachtStatus | null>(item?.verhuurdOfVerpacht?.status ?? null),
                overeenkomstMeerDan12Jaar: new FormControl<boolean | null>(item?.verhuurdOfVerpacht?.overeenkomstMeerDan12Jaar ?? null),
                jaarpacht: new FormControl<number | null>(item?.verhuurdOfVerpacht?.jaarpacht ?? null),
                onzakelijkePacht: new FormControl<boolean | null>(item?.verhuurdOfVerpacht?.onzakelijkePacht ?? null),
                huurbescherming: new FormControl<boolean | null>(item?.verhuurdOfVerpacht?.huurbescherming ?? null),
                jaarhuur: new FormControl<number | null>(item?.verhuurdOfVerpacht?.jaarhuur ?? null),
                onzakelijkeHuur: new FormControl<boolean | null>(item?.verhuurdOfVerpacht?.onzakelijkeHuur ?? null),
                zelfstandigDeelVanGroterGebouw: new FormControl<boolean | null>(item?.soortPand ?? null),
                percentageVerhuurd: new FormControl<number | null>(item?.verhuurdOfVerpacht?.percentageVerhuurd ?? 100),
                directRendement: new FormControl<number | null>(item?.verhuurdOfVerpacht?.directRendement ?? null),
            }),
            verhuurdOfVerpachtOpEinddatum: new FormGroup({
                status: new FormControl<VerhuurdOfVerpachtStatus | null>(item?.verhuurdOfVerpachtOpEinddatum?.status ?? null),
                overeenkomstMeerDan12Jaar: new FormControl<boolean | null>(
                    item?.verhuurdOfVerpachtOpEinddatum?.overeenkomstMeerDan12Jaar ?? null,
                ),
                jaarpacht: new FormControl<number | null>(item?.verhuurdOfVerpachtOpEinddatum?.jaarpacht ?? null),
                onzakelijkePacht: new FormControl<boolean | null>(item?.verhuurdOfVerpachtOpEinddatum?.onzakelijkePacht ?? null),
                huurbescherming: new FormControl<boolean | null>(item?.verhuurdOfVerpachtOpEinddatum?.huurbescherming ?? null),
                jaarhuur: new FormControl<number | null>(item?.verhuurdOfVerpachtOpEinddatum?.jaarhuur ?? null),
                onzakelijkeHuur: new FormControl<boolean | null>(item?.verhuurdOfVerpachtOpEinddatum?.onzakelijkeHuur ?? null),
                zelfstandigDeelVanGroterGebouw: new FormControl<boolean | null>(
                    item?.verhuurdOfVerpachtOpEinddatum?.zelfstandigDeelVanGroterGebouw ?? null,
                ),
                percentageVerhuurd: new FormControl<number | null>(item?.verhuurdOfVerpachtOpEinddatum?.percentageVerhuurd ?? 100),
                directRendement: new FormControl<number | null>(item?.verhuurdOfVerpachtOpEinddatum?.directRendement ?? null),
            }),
        });
    }

    private updateForm(data: OnroerendeZaak[]): void {
        const lijst = data || [];
        const formArray = this.box3OverigeOnroerendeZaken;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(lijst[i], { emitEvent: false });
            } else {
                const formGroup = this.createFormGroup(lijst[i]);
                formArray.push(formGroup, { emitEvent: false });
            }
        }
    }

    saveForm(): void {
        this.formGroup.updateValueAndValidity({ emitEvent: false });

        const mapVerhuurdOfVerpacht = (value: any, isVerhuurdOfVerpacht: boolean) => {
            if (!isVerhuurdOfVerpacht) {
                return undefined;
            }

            if (
                !value ||
                (value.jaarpacht == null && value.jaarhuur == null && value.onzakelijkeHuur == null && value.onzakelijkePacht == null)
            ) {
                return undefined;
            }

            return {
                status: value.status ?? undefined,
                overeenkomstMeerDan12Jaar: value.overeenkomstMeerDan12Jaar ?? undefined,
                jaarpacht: value.jaarpacht ?? undefined,
                onzakelijkePacht: value.onzakelijkePacht ?? undefined,
                huurbescherming: value.huurbescherming ?? undefined,
                jaarhuur: value.jaarhuur ?? undefined,
                onzakelijkeHuur: value.onzakelijkeHuur ?? undefined,
                percentageVerhuurd: value.percentageVerhuurd ?? undefined,
                directRendement: value.directRendement ?? undefined,
            };
        };

        const woningen: OnroerendeZaak[] = this.formGroup
            .getRawValue()
            .box3OverigeOnroerendeZaken.filter(
                (item) => item.adres?.straat?.trim() || item.adres?.postcode?.trim() || item.adres?.huisNummer != null || item.woz != null,
            )
            .map((item, index) => {
                const woznummer = `${index}${item.adres?.postcode ?? '1111'}${item.adres?.huisNummer ?? '2222'}${item.woz ?? 3333}`
                    .replace(/\D/g, '')
                    .slice(0, 12)
                    .padStart(12, '0');

                return {
                    adres: {
                        straat: item.adres?.straat ?? undefined,
                        huisNummer: item.adres?.huisNummer ?? undefined,
                        huisNummerToevoeging: item.adres?.huisNummerToevoeging ?? undefined,
                        postcode: item.adres?.postcode ?? undefined,
                        woonplaats: item.adres?.woonplaats ?? undefined,
                        landCode: item.adres?.landCode ?? undefined,
                    },
                    woz: item.woz ?? 0,
                    wozPlusEen: item.wozPlusEen ?? undefined,
                    woznummer,
                    percentageEigendom: item.percentageEigendom ?? 100,
                    datumGekocht: item.datumGekocht ?? undefined,
                    datumVerkocht: item.datumVerkocht ?? undefined,
                    betaaldeErfpachtCanon: item.betaaldeErfpachtCanon ?? undefined,
                    betaaldeErfpachtCanonEindeJaar: item.betaaldeErfpachtCanonEindeJaar ?? undefined,
                    soortPand: item.soortPand ?? false,
                    verhuurdOfVerpacht: mapVerhuurdOfVerpacht(item.verhuurdOfVerpacht, !!item.isVerhuurdOfVerpacht),
                    verhuurdOfVerpachtOpEinddatum: mapVerhuurdOfVerpacht(item.verhuurdOfVerpachtOpEinddatum, !!item.isVerhuurdOfVerpacht),
                    investeringen: item.investeringen ?? undefined,
                    waardestijgingWOZwaardeWegensInvesteringen: item.waardestijgingWOZwaardeWegensInvesteringen ?? undefined,
                    woning: item.woning ?? undefined,
                    directRendement: item.directRendement ?? undefined,
                };
            });

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            return {
                ...currentEntity,
                aangifte: {
                    ...currentEntity.aangifte,
                    box3: {
                        ...currentEntity.aangifte.box3,
                        overigeOnroerendeZaken: woningen,
                    },
                },
            } as IncomeTaxEntity;
        });
    }

    formatPostcode(postcode: string | null | undefined): string {
        if (!postcode) return '';
        const cleanedPostcode = postcode.replace(/[^a-zA-Z0-9]/g, '');
        if (cleanedPostcode.length >= 4) {
            const cijfers = cleanedPostcode.substring(0, 4).replace(/\D/g, ''); // Alleen cijfers
            const letters = cleanedPostcode
                .substring(4, 6)
                .replace(/[^a-zA-Z]/g, '')
                .toUpperCase(); // Alleen letters
            return `${cijfers}${letters}`;
        }
        return cleanedPostcode;
    }

    getOWRDialogTitel(i: number): string {
        const postcode = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.postcode')?.value;
        const huisNummer = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.huisNummer')?.value;
        const toevoeging = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.huisNummerToevoeging')?.value;
        const woonplaats = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.woonplaats')?.value;

        if (!postcode || !huisNummer) {
            return 'Werkelijk rendement onroerende zaak';
        }

        return `Werkelijk rendement onroerende zaak: ${postcode} ${huisNummer}${toevoeging ? ' ' + toevoeging : ''}${woonplaats ? ', ' + woonplaats : ''}`;
    }

    getGegevensDialogTitel(i: number): string {
        const postcode = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.postcode')?.value;
        const huisNummer = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.huisNummer')?.value;
        const toevoeging = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.huisNummerToevoeging')?.value;
        const woonplaats = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.woonplaats')?.value;
        if (!postcode || !huisNummer) {
            return 'Gegevens onroerende zaak';
        }

        return `Gegevens onroerende zaak: ${postcode} ${huisNummer}${toevoeging ? ' ' + toevoeging : ''}${woonplaats ? ', ' + woonplaats : ''}`;
    }

    getBerekendeWerkelijkRendement(i: number): number | null {
        const postcode = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.postcode')?.value;
        const huisNummer = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.huisNummer')?.value;
        const woonplaats = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.woonplaats')?.value;
        const landCode = this.box3OverigeOnroerendeZaken.controls[i]?.get('adres.landCode')?.value;

        if (!postcode || !huisNummer || !woonplaats) return null;

        const opbouw = this.rapport?.specificatieBox3?.opbouw;
        const lijst = landCode === 'NLD' ? opbouw?.['waardePerOGNederland'] : opbouw?.['waardePerOGBuitenland'];

        const match = lijst?.find(
            (og: OgBerekendeWaarde) =>
                og.adres?.postcode === postcode && og.adres?.huisNummer === huisNummer && og.adres?.woonplaats === woonplaats,
        );

        return match?.werkelijkRendement ?? null;
    }

    getDatumBegin(i: number): string {
        const value = this.box3OverigeOnroerendeZaken.controls[i]?.get('datumGekocht')?.value;
        if (!value) {
            return '1-1-' + this.jaar;
        }
        const date = new Date(value);
        return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
    }

    getDatumEind(i: number): string {
        const value = this.box3OverigeOnroerendeZaken.controls[i]?.get('datumVerkocht')?.value;
        if (!value) {
            return '31-12-' + this.jaar;
        }
        const date = new Date(value);
        return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
    }
}
